import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

import type { LoadAdditionalItems } from '../components/filterable-gallery/src/types';
import type { SearchConfiguration } from '../components/filterable-gallery/src/utils/filterEngine';

export interface FilterableGalleryLoaderRegistration {
	loader: LoadAdditionalItems;
}

/**
 * The gallery's items + search config + featured slugs, published for the
 * sibling search bar so both run the same search pipeline over the same data.
 */
export interface FilterableGalleryConfig {
	items: unknown[];
	searchConfiguration?: SearchConfiguration;
	featuredItemSlugs: string[];
}

interface FilterableGalleryLoaderContextValue {
	currentRegistration: FilterableGalleryLoaderRegistration | null;
	extraItems: unknown[];
	galleryConfig: FilterableGalleryConfig | null;
	registerGalleryConfig: (config: FilterableGalleryConfig) => void;
}

const FilterableGalleryLoaderContext = createContext<FilterableGalleryLoaderContextValue | null>(
	null,
);

interface FilterableGalleryLoaderProviderProps {
	currentRegistration?: FilterableGalleryLoaderRegistration | null;
	extraItems?: unknown[];
}

// Module-level constant so the default is the SAME reference on every render.
// A `= []` default parameter allocates a fresh array on every call — when a
// caller (e.g. the gallery self-wrapping without a `contentfulGalleryContainer`)
// never passes `extraItems` explicitly, every render of this provider handed a
// new default array, which cascaded into consumers keyed on it by reference
// and re-triggered this provider's own state updates: a self-sustaining loop.
const DEFAULT_EXTRA_ITEMS: unknown[] = [];

export const FilterableGalleryLoaderProvider = ({
	children,
	currentRegistration = null,
	extraItems = DEFAULT_EXTRA_ITEMS,
}: PropsWithChildren<FilterableGalleryLoaderProviderProps>): JSX.Element => {
	// Owned internally so neither the container nor the standalone gallery has
	// to thread it through props; the gallery publishes, the search bar reads.
	const [galleryConfig, setGalleryConfig] = useState<FilterableGalleryConfig | null>(null);

	const value = useMemo<FilterableGalleryLoaderContextValue>(
		() => ({
			currentRegistration,
			extraItems,
			galleryConfig,
			registerGalleryConfig: setGalleryConfig,
		}),
		[currentRegistration, extraItems, galleryConfig],
	);
	return (
		<FilterableGalleryLoaderContext.Provider value={value}>
			{children}
		</FilterableGalleryLoaderContext.Provider>
	);
};

export const useFilterableGalleryLoader = (): FilterableGalleryLoaderContextValue | null =>
	useContext(FilterableGalleryLoaderContext);

export const useIsInsideLoaderProvider = (): boolean =>
	useContext(FilterableGalleryLoaderContext) !== null;
